import { fetchAuthSession, signOut } from 'aws-amplify/auth';

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again.';
export const SESSION_EXPIRED_EVENT = 'portinsider:session-expired';
const storageKey = 'portinsider:session-expired';
let expiring = false;
let logout: Promise<void> | undefined;
let refresh: ReturnType<typeof fetchAuthSession> | undefined;
let redirectUrl = '/';
let logoutUrl = '/';
let userPoolClientId: string | undefined;
export const SIGN_OUT_TIMEOUT_MS = 5000;

export function configureSessionExpiry(
  url: string,
  cognito?: { domain: string; clientId: string },
) {
  redirectUrl = url || '/';
  userPoolClientId = cognito?.clientId;
  if (cognito?.domain && cognito.clientId) {
    const domain = cognito.domain
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '');
    const params = new URLSearchParams({
      client_id: cognito.clientId,
      logout_uri: redirectUrl,
    });
    logoutUrl = `https://${domain}/logout?${params.toString()}`;
  } else {
    logoutUrl = redirectUrl;
  }
}

// The app uses Amplify's default localStorage token store. On timeout the SDK
// may still be waiting for token revocation, before it clears local credentials.
// Remove only this client/user's session tokens so the return to login cannot
// incorrectly reuse them. Never clear unrelated application storage.
function clearFallbackCredentials(clientId: string | undefined) {
  if (!clientId) return;
  try {
    const prefix = `CognitoIdentityServiceProvider.${clientId}.`;
    const user = localStorage.getItem(`${prefix}LastAuthUser`);
    if (!user) return;
    for (const key of ['accessToken', 'idToken', 'refreshToken']) {
      localStorage.removeItem(`${prefix}${user}.${key}`);
    }
    localStorage.removeItem(`${prefix}LastAuthUser`);
  } catch {
    // Continue hosted logout even if the browser has disabled local storage.
  }
}

export function hasExpiredSession(): boolean {
  try {
    return expiring || sessionStorage.getItem(storageKey) === 'true';
  } catch {
    return expiring;
  }
}

export function clearExpiredSession() {
  expiring = false;
  logout = undefined;
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // The in-memory state also supports browsers that block storage.
  }
}

export function rememberExpiredSession() {
  try {
    sessionStorage.setItem(storageKey, 'true');
  } catch {
    // The current page can still display the notice without storage.
  }
}

export function isSessionExpiring() {
  return expiring;
}

export function isAuthenticationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const value = error as {
    name?: string;
    status?: number;
    statusCode?: number;
    networkError?: unknown;
    response?: { status?: number };
    graphQLErrors?: readonly unknown[];
    errors?: readonly unknown[];
    extensions?: { code?: string };
  };
  return (
    value.status === 401 ||
    value.statusCode === 401 ||
    value.response?.status === 401 ||
    [
      'UserUnAuthenticatedException',
      'NotAuthorizedException',
      'SessionExpiredError',
      'TokenRevokedException',
      'UserNotFoundException',
      'PasswordResetRequiredException',
      'UserNotConfirmedException',
      'RefreshTokenReuseException',
    ].some((name) => value.name?.startsWith(name)) ||
    value.extensions?.code === 'UNAUTHENTICATED' ||
    isAuthenticationError(value.networkError) ||
    !!value.graphQLErrors?.some(isAuthenticationError) ||
    !!value.errors?.some(isAuthenticationError)
  );
}

export class SessionExpiredError extends Error {
  constructor() {
    super(SESSION_EXPIRED_MESSAGE);
    this.name = 'SessionExpiredError';
  }
}

export function expireSession(): Promise<void> {
  if (logout) return logout;
  expiring = true;
  rememberExpiredSession();
  const startingUrl = window.location.href;
  const fallbackUrl = logoutUrl;
  const clientId = userPoolClientId;
  // Assign the promise before notifying listeners to prevent reentrant logout.
  logout = new Promise<void>((resolve) => {
    let finished = false;
    const finish = (clearCredentials: boolean) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timeout);
      // Amplify's hosted logout sets location.href itself. Preserve that
      // navigation instead of immediately replacing it with the application URL.
      if (window.location.href === startingUrl) {
        if (clearCredentials) clearFallbackCredentials(clientId);
        window.location.href = fallbackUrl;
      }
      resolve();
    };
    const timeout = window.setTimeout(() => finish(true), SIGN_OUT_TIMEOUT_MS);
    void Promise.resolve()
      .then(() => signOut())
      .then(
        () => finish(false),
        () => finish(true),
      );
  });
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  return logout;
}

/** Amplify checks expiry; share refresh work across requests and the timer. */
export async function getValidToken(forceRefresh = false): Promise<string> {
  if (expiring) throw new SessionExpiredError();
  try {
    if (!refresh) {
      refresh = fetchAuthSession({ forceRefresh });
    }
    const pending = refresh;
    let session;
    try {
      session = await pending;
    } finally {
      if (refresh === pending) refresh = undefined;
    }
    if (expiring) throw new SessionExpiredError();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new SessionExpiredError();
    return token;
  } catch (error) {
    if (isAuthenticationError(error)) void expireSession();
    throw error;
  }
}

export function handleAuthenticationError(error: unknown): boolean {
  if (isAuthenticationError(error)) {
    void expireSession();
    return true;
  }
  return isSessionExpiring();
}

export async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
) {
  const headers = new Headers(options.headers);
  headers.set('Authorization', await getValidToken());
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    void expireSession();
    throw new SessionExpiredError();
  }
  return response;
}

export function sendAuthenticatedUpload(
  xhr: XMLHttpRequest,
  body: File,
  onFailure: () => void,
) {
  void getValidToken()
    .then((token) => {
      if (isSessionExpiring()) return;
      xhr.setRequestHeader('Authorization', token);
      xhr.send(body);
    })
    .catch((error) => {
      if (!handleAuthenticationError(error)) onFailure();
    });
}
